import React, { useState, useRef, useEffect } from 'react';
import { StopIcon, MicrophoneIcon } from '@heroicons/react/24/solid';
import { useTranslation } from 'react-i18next';
import { PaperAirplaneIcon } from '@heroicons/react/20/solid';

// Add onTranscriptionComplete prop
interface AudioRecorderProps {
  onTranscriptionComplete: (transcription: string) => void;
  enablePulseAnimation?: boolean;
}

export const AudioRecorder: React.FC<AudioRecorderProps> = ({
  onTranscriptionComplete,
  enablePulseAnimation = false,
}) => {
  const { t } = useTranslation('onboarding');

  const [isRecording, setIsRecording] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [showInitialPulse, setShowInitialPulse] =
    useState(enablePulseAnimation);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);

  const startRecording = async () => {
    if (showInitialPulse) {
      setShowInitialPulse(false);
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/wav' });
        audioChunksRef.current = []; // Clear chunks for the next recording

        setIsRecording(false); // Update UI from "Recording..." state
        handleSendAudio(blob); // Automatically send the audio

        // Stop the stream tracks after blob is created and sending is initiated
        stream.getTracks().forEach((track) => track.stop());
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
    } catch (err) {
      console.error('Error accessing microphone:', err);
      setIsRecording(false);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      // onstop handles setIsRecording(false) and initiates sending
    }
  };

  // --- handleSendAudio logic (modified to accept blob) ---
  const handleSendAudio = async (blobToSend: Blob) => {
    if (!blobToSend) {
      console.error('handleSendAudio called without a blob');
      setIsSending(false); // Ensure sending state is reset if called incorrectly
      return;
    }
    setIsSending(true);

    const formData = new FormData();
    // Use the passed blobToSend instead of state
    const audioFile = new File([blobToSend], 'audio.wav', {
      type: 'audio/wav',
    });
    formData.append('file', audioFile);
    formData.append('language', navigator.language);

    try {
      // Add try-catch for fetch
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/files/transcript_audio`,
        {
          method: 'POST',
          body: formData,
          headers: {
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          },
        },
      );

      if (!response.ok) {
        throw new Error(`Upload failed with status: ${response.status}`);
      }

      // Handle successful response (e.g., get transcription)
      const data = await response.json(); // Parse the JSON response

      // Call the callback prop with the suggestion
      if (data && data.data && data.data.transcription) {
        // Check nested structure
        onTranscriptionComplete(data.data.transcription);
      }
    } catch (error) {
      console.error('Error sending audio:', error);
      // TODO: Handle upload error state in UI
    } finally {
      setIsSending(false); // Stop sending
    }
  };
  // --- End handleSendAudio logic ---

  if (!isRecording && !isSending) {
    return (
      <div className="flex items-center">
        <button
          type="button"
          onClick={startRecording}
          className={`btn btn-circle btn-xs ${
            showInitialPulse ? 'animate-shadow-pulse' : ''
          }`}
          aria-label={t('audio_recorder.start_recording')}
        >
          <MicrophoneIcon className="h-4 w-4" />
        </button>
      </div>
    );
  }

  if (isRecording && !isSending) {
    return (
      <button
        type="button"
        onClick={stopRecording}
        className="btn btn-circle btn-error btn-xs flex items-center justify-center"
        aria-label={t('audio_recorder.stop_recording')}
      >
        <StopIcon className="h-3 w-3 text-white" />
      </button>
    );
  }

  {
    /* Mostrar indicador de envío si no está grabando pero está enviando */
  }
  if (!isRecording && isSending) {
    return (
      <button
        type="button"
        className="btn btn-circle btn-xs flex items-center"
        aria-label={t('audio_recorder.sending')}
      >
        <PaperAirplaneIcon className="h-3 w-3 animate-pulse" />
      </button>
    );
  }

  // Default return to ensure the component always returns a ReactElement
  return null;
};
